/**
 * Self-healing for stale deployments. The bundle is code-split into content-hashed
 * chunks; a redeploy replaces them all, so a browser holding the previous index.html
 * (or a long-lived tab) requests chunk files that no longer exist and every lazy route
 * import rejects — the user is wedged on the error fallback until a manual reload.
 *
 * Two hooks feed the same one-shot reload:
 * - `installStaleChunkAutoReload` listens for Vite's `vite:preloadError` (fired when a
 *   dynamic-import dependency fails to preload).
 * - `isStaleChunkError` lets the error boundary recognise a failed module import that
 *   reached React (message shapes differ per browser, hence the pattern list).
 *
 * A plain reload revalidates index.html and picks up the current chunk set. The
 * sessionStorage timestamp guard makes the reload one-shot per minute per tab, so a
 * genuinely broken deploy degrades to the normal error fallback instead of a loop.
 */

const GUARD_KEY = 'crm.staleChunkReloadAt';
const MIN_RELOAD_INTERVAL_MS = 60_000;

/** Cadence for the fallback's hands-off retry loop — just above the reload guard. */
export const STALE_CHUNK_RETRY_INTERVAL_MS = MIN_RELOAD_INTERVAL_MS + 5_000;

const STALE_CHUNK_PATTERNS = [
  /failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /expected a javascript.*module script/i, // strict-MIME rejection (HTML served as JS)
  /unable to preload css/i, // Vite CSS dep preload
];

export function isStaleChunkError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return STALE_CHUNK_PATTERNS.some((pattern) => pattern.test(message));
}

function lastReloadAt(): number {
  try {
    return Number(sessionStorage.getItem(GUARD_KEY)) || 0;
  } catch {
    return 0;
  }
}

/** Reload the page to pick up a fresh chunk set; returns false when the guard holds. */
export function reloadForStaleChunk(): boolean {
  if (Date.now() - lastReloadAt() < MIN_RELOAD_INTERVAL_MS) {
    return false;
  }
  try {
    sessionStorage.setItem(GUARD_KEY, String(Date.now()));
  } catch {
    // Storage unavailable (private mode quirks): reload anyway — worst case the user
    // sees the error fallback again instead of an auto-retry loop.
  }
  window.location.reload();
  return true;
}

export function installStaleChunkAutoReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForStaleChunk()) {
      event.preventDefault();
    }
  });
}

/**
 * Boot-time freshness check: a browser may start a session from a heuristically
 * cached index.html WITHOUT revalidating it (no reload happened), booting an app
 * whose chunk set is already gone from the server. Fetch the live index.html
 * (cache-bypassing) and compare the hashed entry script with the one this document
 * actually loaded — on mismatch, one guarded reload picks up the current build.
 */
export async function checkBundleFreshness(): Promise<void> {
  const entryScript = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src*="/assets/"]',
  );
  if (!entryScript) {
    return; // dev server (unhashed /src/main.tsx) — nothing to compare
  }
  try {
    const response = await fetch('/index.html', { cache: 'no-store' });
    if (!response.ok) {
      return;
    }
    const liveHtml = await response.text();
    const loadedEntryPath = new URL(entryScript.src, window.location.href).pathname;
    if (!liveHtml.includes(loadedEntryPath)) {
      reloadForStaleChunk();
    }
  } catch {
    // Offline or transient network failure — the runtime listeners still cover us.
  }
}
